import _isEmpty from 'lodash/isEmpty';
import useWhiteLabelClient from './useWhiteLabelClient';
import utils from '../helpers/utils';

/**
 * Interface for Adobe Target test configuration
 * Used for A/B testing different offers via Adobe Target
 */
interface AdobeTestConfig {
  /** Whether the A/B test is active */
  enabled?: boolean;
  /** Offers to show for users without credit features */
  non_credit_user_offers?: FinancialOffer[];
  /** Offers to show for users with credit features */
  offers_data?: FinancialOffer[];
  credit_insights_offers_data?: CreditInsightsOffer;
}
/**
 * Interface for Adobe Target test configuration for the marketplace for credit and savings offers feature page
 */
interface AdobeOffersMarketplaceTest {
  enabled?: boolean;
  savingsOffersEnabled?: boolean;
}

/**
 * Interface for Adobe Credit Freeze Unlocked Test configuration
 * Used for displaying special content when a user's credit file is unlocked
 */
interface AdobeCreditFreezeUnlockedTest {
  /** Whether the credit freeze unlock test is active */
  enabled?: boolean;
  /** Content to display in the unlock modal */
  unlock_modal_content?: {
    unlocked_title: string;
    unlocked_description: string;
    top_highlight: string;
    recommended_title: string;
    recommended_description: string;
    explore_button_text: string;
    explore_button_url: string;
  };
}

/**
 * Interface for the MoneyLion credit card offers adobe test
 */
interface AdobeMLCardOffersTest {
  enabled?: boolean;
}

interface AdobeCardOffersTUUnlockModalTest {
  enabled?: boolean;
}

/**
 * Interface for a single financial offer
 * Contains all the data needed to display an offer card including
 * text keys for localization, URLs, and vendor information
 */
interface FinancialOffer {
  /** Unique identifier for the offer */
  id: string;
  /** Key for the illustration to display */
  illustrationKey: string;
  /** Key for the header text in half-width view (empty string if not used) */
  halfHeaderTextKey: string;
  /** Key for the header text in full-width view */
  fullHeaderTextKey: string;
  /** Key for the body text in half-width view */
  halfBodyTextKey: string;
  /** Key for the body text in full-width view */
  fullBodyTextKey: string;
  /** Whether this offer should show a credit ratings modal */
  hasCreditRatingsModal: boolean;
  /** Key for the CTA button text in half-width view */
  halfCtaTextKey: string;
  /** Key for the CTA button text in full-width view */
  fullCtaTextKey: string;
  /** Environment variable key for the CTA URL */
  ctaURLKey: string;
  /** Direct URL for the CTA (takes precedence over ctaURLKey) */
  ctaURL: string;
  /** Vendor identifier for analytics */
  ctaVendor: string;
  /** Label used for Google Analytics tracking */
  gaLabel: string;
  /** Localized value for the header text in half-width view */
  halfHeaderTextValue: string;
  /** Localized value for the header text in full-width view */
  fullHeaderTextValue: string;
  /** Localized value for the body text in half-width view */
  halfBodyTextValue: string;
  /** Localized value for the body text in full-width view */
  fullBodyTextValue: string;
  /** Localized value for the CTA button text in half-width view */
  halfCtaTextValue: string;
  /** Localized value for the CTA button text in full-width view */
  fullCtaTextValue: string;
}

/**
 * Represents a financial offer with detailed information for credit insights.
 */
interface CreditInsightsOffer {
  /** Title for the credit insights offer */
  credit_insights_offers_title: string;
  /** CTA text for the credit insights offer */
  credit_insights_offers_cta_text: string;
}
interface FinancialOffersHookResult {
  /** Whether credit offers should be shown to the user */
  isCreditOffersActive: boolean;
  /** Array of financial offers to display */
  creditOffersData: FinancialOffer[];
  /** Whether the credit insights offers should be shown */
  isCreditInsightsOffersActive: boolean;
  /** Credit insights offer data */
  creditInsightsOffersData?: CreditInsightsOffer;
  /** Whether the credit freeze unlock feature is active */
  isCreditFreezeUnlockActive: boolean;
  /** Content to display in the credit freeze unlock modal */
  creditFreezeUnlockContent?: {
    unlocked_title: string;
    unlocked_description: string;
    top_highlight: string;
    recommended_title: string;
    recommended_description: string;
    explore_button_text: string;
    explore_button_url: string;
  };
  /** Whether MoneyLion card offers should be shown */
  isMLCardOffersActive: boolean;
  isCardOffersTUUnlockModalActive: boolean;
  isCreditOffersMarketplaceActive: boolean;
  isSavingsOffersMarketplaceActive: boolean;
}

/**
 * Default list of financial offers with localized text values and metadata.
 *
 * @returns An array of `FinancialOffer` objects, each representing a financial offer
 * with localized text values and associated metadata.
 */
export const defaultCreditOffersData = [
  {
    id: 'virtual-credit-cards',
    illustrationKey: 'credit_cards',
    halfHeaderTextKey: '',
    fullHeaderTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_HEADER',
    halfBodyTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_MINI_TAGLINE',
    fullBodyTextKey: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_TAGLINE',
    hasCreditRatingsModal: false,
    halfCtaTextKey: 'EXPLORE',
    fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_CREDIT_CARDS',
    ctaURLKey: 'REACT_APP_SAFER_ONLINE_TRANSACTIONS',
    ctaURL: window.REACT_APP_SAFER_ONLINE_TRANSACTIONS || '',
    ctaVendor: 'cardratings',
    gaLabel: 'Security Credit Card',

    halfHeaderTextValue: '',
    fullHeaderTextValue: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_HEADER',
    halfBodyTextValue: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_MINI_TAGLINE',
    fullBodyTextValue: 'FINANCIAL_OFFERS_VIRTUAL_CREDIT_CARD_TAGLINE',
    halfCtaTextValue: 'EXPLORE',
    fullCtaTextValue: 'FINANCIAL_OFFERS_EXPLORE_CREDIT_CARDS',
  },
  {
    id: 'high-interest-savings',
    illustrationKey: 'bank',
    halfHeaderTextKey: '',
    fullHeaderTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_HEADER',
    halfBodyTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_MINI_TAGLINE',
    fullBodyTextKey: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_TAGLINE',
    hasCreditRatingsModal: false,
    halfCtaTextKey: 'EXPLORE',
    fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_SAVINGS',
    ctaURLKey: 'REACT_APP_HIGH_YIELD_SAVING_ACCOUNT_VENDOR_URL',
    ctaURL: window.REACT_APP_HIGH_YIELD_SAVING_ACCOUNT_VENDOR_URL || '',
    ctaVendor: 'mybanktracker',
    gaLabel: 'Saving Accounts',

    halfHeaderTextValue: '',
    fullHeaderTextValue: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_HEADER',
    halfBodyTextValue: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_MINI_TAGLINE',
    fullBodyTextValue: 'FINANCIAL_OFFERS_HIGH_INTEREST_SAVINGS_TAGLINE',
    halfCtaTextValue: 'EXPLORE',
    fullCtaTextValue: 'FINANCIAL_OFFERS_EXPLORE_SAVINGS',
  },
  {
    id: 'travel-benefits',
    illustrationKey: 'plane',
    halfHeaderTextKey: '',
    fullHeaderTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_HEADER',
    halfBodyTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_MINI_TAGLINE',
    fullBodyTextKey: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_TAGLINE',
    hasCreditRatingsModal: true,
    halfCtaTextKey: 'EXPLORE',
    fullCtaTextKey: 'FINANCIAL_OFFERS_EXPLORE_TRAVEL_BENEFITS',
    ctaURLKey: 'REACT_APP_CREDIT_TRAVEL_BENEFITS_VENDOR_URL',
    ctaURL: window.REACT_APP_CREDIT_TRAVEL_BENEFITS_VENDOR_URL || '',
    ctaVendor: 'cardratings',
    gaLabel: 'Travel Credit Card',

    halfHeaderTextValue: '',
    fullHeaderTextValue: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_HEADER',
    halfBodyTextValue: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_MINI_TAGLINE',
    fullBodyTextValue: 'FINANCIAL_OFFERS_TRAVEL_BENEFITS_TAGLINE',
    halfCtaTextValue: 'EXPLORE',
    fullCtaTextValue: 'FINANCIAL_OFFERS_EXPLORE_TRAVEL_BENEFITS',
  },
];

/**
 * Custom hook for managing financial offers functionality
 *
 * This hook determines whether financial offers should be displayed to the user and
 * which offers should be shown based on various criteria:
 *
 * 1. User's product features and permissions
 * 2. Adobe Target A/B test configuration
 * 3. White label client status
 * 4. User's credit status
 *
 * The hook handles localization of offer content and supports both default offers
 * and dynamically configured offers from Adobe Target.
 *
 * @param {ProductFeatures} productFeatures - User's product features and permissions
 * @param {Record<string, string>} localizedStringBundle - Required string bundle for localization
 *
 * @returns {Object} Financial offers configuration
 * @returns {boolean} .isCreditOffersActive - Whether financial offers should be shown
 * @returns {boolean} isMLCardOffersActive - Whether MoneyLion card offers should be shown
 * @returns {FinancialOffer[]} .creditOffersData - Array of offers to display
 *
 * @example
 * // Basic usage
 * const { isCreditOffersActive, creditOffersData } = useFinancialOffers(userFeatures, stringBundle);
 * if (isCreditOffersActive) {
 *   // Render offers using creditOffersData
 * }
 *
 * @example
 * // With custom string bundle
 * const customStrings = { 'EXPLORE': 'Discover Now' };
 * const { creditOffersData } = useFinancialOffers(userFeatures, customStrings);
 */
export const useFinancialOffers = (
  productFeatures: ProductFeatures,
  _localizedStringBundle: Record<string, string>
): FinancialOffersHookResult => {
  const {adobeCreditOffersTest = {}} = window as Window & {
    adobeCreditOffersTest?: AdobeTestConfig;
  };

  const {adobeCreditFreezeUnlockedTest = {}} = window as Window & {
    adobeCreditFreezeUnlockedTest?: AdobeCreditFreezeUnlockedTest;
  };

  const {adobeMLCardOffersTest = {}} = window as Window & {
    adobeMLCardOffersTest?: AdobeMLCardOffersTest;
  };

  const {adobeCardOffersTUUnlockModalTest = {}} = window as Window & {
    adobeCardOffersTUUnlockModalTest?: AdobeCardOffersTUUnlockModalTest;
  };

  const {adobeCreditInsightsModalTest = {}} = window as Window & {
    adobeCreditInsightsModalTest?: AdobeTestConfig;
  };

  const {adobeOffersMarketplace = {}} = window as Window & {
    adobeOffersMarketplace?: AdobeOffersMarketplaceTest;
  };

  const hasFinancialOffers = utils.hasFinancialOffers(productFeatures);
  const isCreditOffersTestActive = (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.enabled) || false;

  const isCreditFreezeUnlockActive =
    (hasFinancialOffers && !_isEmpty(adobeCreditFreezeUnlockedTest) && adobeCreditFreezeUnlockedTest.enabled) || false;
  const isMLCardOffersActive = (!_isEmpty(adobeMLCardOffersTest) && adobeMLCardOffersTest.enabled) || false;
  const isCardOffersTUUnlockModalActive =
    (!_isEmpty(adobeCardOffersTUUnlockModalTest) && adobeCardOffersTUUnlockModalTest.enabled) || false;
  const isCreditInsightsOffersActive =
    (hasFinancialOffers && !_isEmpty(adobeCreditInsightsModalTest) && adobeCreditInsightsModalTest.enabled) || false;

  const whiteLabelClient: string = useWhiteLabelClient();
  const isNonCreditUser = !utils.isCreditUser(productFeatures);

  const creditOffersData = isNonCreditUser
    ? (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.non_credit_user_offers) || []
    : (!_isEmpty(adobeCreditOffersTest) && adobeCreditOffersTest.offers_data) || [];

  // If the marketplace for credit offers feature page is enabled (different than the card offers used to populate the card carousel on credit pages)
  const isCreditOffersMarketplaceActive =
    (!_isEmpty(adobeOffersMarketplace) && adobeOffersMarketplace.enabled) || false;
  // If the marketplace for savings offers feature page is enabled
  const isSavingsOffersMarketplaceActive =
    (!_isEmpty(adobeOffersMarketplace) && adobeOffersMarketplace.savingsOffersEnabled) || false;
  // Old credit offers will only be available if new ML credit offers is not enabled and the rest of the conditions are met
  return {
    isCreditOffersActive: !isMLCardOffersActive && hasFinancialOffers && isCreditOffersTestActive && !whiteLabelClient,
    isCardOffersTUUnlockModalActive,
    creditOffersData,
    isCreditInsightsOffersActive: isMLCardOffersActive && isCreditInsightsOffersActive,
    creditInsightsOffersData: adobeCreditInsightsModalTest.credit_insights_offers_data,
    isCreditFreezeUnlockActive,
    creditFreezeUnlockContent: adobeCreditFreezeUnlockedTest.unlock_modal_content,
    isMLCardOffersActive: hasFinancialOffers && isMLCardOffersActive && !whiteLabelClient,
    isCreditOffersMarketplaceActive,
    isSavingsOffersMarketplaceActive,
  };
};
